import { logger } from '../utils/logger';

export const DEPLOY_RELOAD_STORAGE_KEY = 'gta_deploy_reload';
const RELOAD_COOLDOWN_MS = 15_000;
const VERSION_POLL_MS = 5 * 60_000;

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk .* failed/i,
  /Loading CSS chunk .* failed/i,
  /Failed to load module script/i,
  /MIME type/i,
  /Importing a module script failed/i,
];

export function isDeployStaleLoadError(message: string): boolean {
  const text = message.trim();
  if (!text) return false;
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(text));
}

export function canAttemptDeployReload(now = Date.now()): boolean {
  try {
    const last = sessionStorage.getItem(DEPLOY_RELOAD_STORAGE_KEY);
    if (!last) return true;
    const elapsed = now - Number.parseInt(last, 10);
    return !Number.isFinite(elapsed) || elapsed > RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
}

export function markDeployReloadAttempt(now = Date.now()): void {
  try {
    sessionStorage.setItem(DEPLOY_RELOAD_STORAGE_KEY, String(now));
  } catch {
    // Private mode / disabled storage — still attempt one reload.
  }
}

/** Cache-busting navigation (closest safe equivalent to hard refresh). */
export function reloadForDeployUpdate(reason?: string): void {
  if (!canAttemptDeployReload()) {
    logger.warn('[deploy] Skipping auto-reload (cooldown active)', reason);
    return;
  }
  markDeployReloadAttempt();
  logger.log('[deploy] Reloading for new build…', reason ?? '');
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_deploy', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

async function clearCacheStorage(): Promise<void> {
  if (!('caches' in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // Best-effort only.
  }
}

export function installDeployUpdateRecovery(): void {
  if (typeof window === 'undefined') return;

  const onError = (event: ErrorEvent) => {
    const target = event.target;
    if (target instanceof HTMLScriptElement) {
      void clearCacheStorage().finally(() => reloadForDeployUpdate('script-element-error'));
      return;
    }
    if (isDeployStaleLoadError(event.message ?? '')) {
      void clearCacheStorage().finally(() => reloadForDeployUpdate(event.message));
    }
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : String(reason);
    if (isDeployStaleLoadError(message)) {
      void clearCacheStorage().finally(() => reloadForDeployUpdate(message));
    }
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection);
}

async function fetchDeployedVersion(): Promise<string | null> {
  const base = import.meta.env.BASE_URL || '/';
  const url = `${base}version.json?t=${Date.now()}`;
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
    });
    if (!response.ok) return null;
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) return null;
    const data = (await response.json()) as { version?: string };
    return typeof data.version === 'string' && data.version ? data.version : null;
  } catch {
    return null;
  }
}

let versionWatchStarted = false;

/** Polls `version.json` and reloads when a new build is deployed. */
export function startDeployVersionWatch(runningVersion: string): void {
  if (typeof window === 'undefined' || versionWatchStarted || !runningVersion) return;
  versionWatchStarted = true;

  const check = async () => {
    const remote = await fetchDeployedVersion();
    if (!remote || remote === runningVersion) return;
    reloadForDeployUpdate(`version ${runningVersion} -> ${remote}`);
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  window.setInterval(() => void check(), VERSION_POLL_MS);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', () => void check());
}
